import React from "react";
import { round } from "lodash";
import {
  shouldHaveE1RM,
  formattedTarget,
  getSetCounts,
  outcomesAreEqual,
  hasEqualOutcomes,
  getMetaCounts,
  getVelocityValue,
} from "../../../../utils/actuals";
import {
  mapParameter,
  parameterName,
} from "../../../../parts/other-exercise/CustomParametersSets";
import ExerciseVideoLine from "../../program-builder/components/ExerciseVideoLine";
import CommentComponent from "./CommentComponent";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import SvgIcon from "../../../../parts/SvgIcon";
import ActualResultsHead from "./ActualResultsHead";

const {
  PARAMETER_OPTIONS: { ROUNDS, SETS, LOAD, RPE, RPEAMRAP, FREEFORM },
} = require("../../../../constants");

const StatusIcon = ({ status }) => {
  return status === "missed" ? (
    <SvgIcon
      src="/img/new-icons/info-circle.svg"
      classNames={status}
      noFilterOnLight={true}
    />
  ) : !status ? null : (
    <SvgIcon
      src="/img/new-icons/tick-square.svg"
      classNames={status}
      noFilterOnLight={true}
    />
  );
};

const OutcomesComponent = (props) => {
  const {
    exercise,
    set,
    units,
    withWeight,
    fromMetric,
    idx,
    targetLoad,
    targetRM,
    expanded,
    intensityMeasure,
    isRiR,
    hideVelocitySettings,
    exerciseName,
    prefix,
    toggleExpanded,
  } = props;
  const {
    _id: exerciseId,
    parameters,
    primary,
    repMaximums,
    secondaryRepMaximum,
    type,
  } = exercise;

  const showE1RM = shouldHaveE1RM(exercise);
  const showPercentRM = type === "primary" || type === "secondary";
  const defaultRepMax =
    type === "secondary" && secondaryRepMaximum
      ? secondaryRepMaximum
      : repMaximums[primary] || 0;

  const { total, completed, missed } = getSetCounts(exercise, set);
  const equalOutcomes = outcomesAreEqual(set);
  const equalOutcomesEx = hasEqualOutcomes(exercise);

  const isExpanded = (equalOutcomes && expanded) || !equalOutcomes;
  const setsOrRounds = isExpanded ? Number(set.sets || set.rounds || 1) : 1;

  return Array.from({ length: setsOrRounds }, (x, i) => {
    const outcomes = (set.outcomes && set.outcomes[i]) || { outcome: {} };

    const { outcome, status } = outcomes;

    const { reps, rpe, load, e1rm, velocity } = outcome || {};

    const comment = (set.comments && set.comments[i]) || "";

    const video = set.videos && set.videos[i];

    const { outcomeCount, commentCount, videoCount } = getMetaCounts(set);

    return (
      <div
        className={`actuals-outcomes-container set${i}`}
        key={`setOrRound${i}`}
      >
        <div className="target-status">
          {i === 0 && (
            <>
              <span
                className={
                  completed + missed === total
                    ? "circle-status completed"
                    : missed === 0 && completed === 0
                    ? "circle-status target"
                    : "circle-status partial"
                }
              ></span>
              <p className="p4">
                {formattedTarget(
                  set,
                  showPercentRM,
                  targetRM,
                  units,
                  targetLoad,
                  intensityMeasure,
                  isRiR
                )}
              </p>
            </>
          )}
        </div>
        <div className="actuals-titles only-show-mobile">
          <p className="p4">Results</p>
          <p className="p4">#</p>
          {parameters ? (
            Array.from({ length: parameters.length }, (x, i) =>
              [FREEFORM, ROUNDS, SETS].includes(parameters[i]) ? null : (
                <p
                  key={`${i}-${exercise._id}-title`}
                  className="p4 mobile-hidden"
                >
                  {parameterName(parameters[i], isRiR)}
                </p>
              )
            )
          ) : (
            <>
              <p className="p4">Reps</p>
              <p className="p4">{intensityMeasure}</p>
              <p className="p4">Load</p>
            </>
          )}
          {showPercentRM && <p className="p4 mobile-hidden">%1RM</p>}
          {showE1RM && <p className="p4 mobile-hidden">e1RM</p>}
          {showPercentRM && !hideVelocitySettings && (
            <p className="p4mobile-hidden">Vel.</p>
          )}
          {parameters && parameters[0] == FREEFORM && (
            <p className="p4 freeform mobile-hidden"></p>
          )}
          <p className="p4 video">Video</p>
          <p className="p4 description mobile-hidden">Comment</p>
          {equalOutcomes && (
            <p
              className="p4 expand-outcomes mobile-hidden"
              onClick={toggleExpanded}
            >
              {expanded ? "See less" : "Expand"}
            </p>
          )}
        </div>
        <div className="actuals-outcomes">
          <p className="p4 actuals-status">
            <StatusIcon status={status} />
          </p>
          <p className="p4">{isExpanded ? i + 1 : `1 - ${outcomeCount}`}</p>
          {parameters ? (
            parameters.map((p) => {
              if (p === ROUNDS || p === SETS) return null;
              const parameter = mapParameter(p);
              const value = outcome ? outcome[parameter] : null;
              if (p === LOAD) {
                return (
                  <p
                    key={`${idx}-set${i}-${exerciseId}-param-${p}`}
                    className="p4 mobile-hidden"
                  >
                    {value
                      ? withWeight(
                          round(fromMetric(value, units, false), 1),
                          units,
                          false
                        )
                      : ""}
                  </p>
                );
              }
              return (
                <p
                  key={`${idx}-set${i}-${exerciseId}-param-${p}`}
                  className={`p4 ${
                    parameter === "freeform" ? "freeform" : ""
                  } mobile-hidden`}
                >
                  {[RPE, RPEAMRAP, "rpe"].includes(parameter)
                    ? convertToRiR(value, !isRiR)
                    : value}
                </p>
              );
            })
          ) : (
            <>
              <p className="p4">{reps}</p>
              <p className="p4">{convertToRiR(rpe, !isRiR)}</p>
              <p className="p4">
                {load
                  ? withWeight(
                      round(fromMetric(load, units, false), 1),
                      units,
                      false
                    )
                  : ""}
              </p>
            </>
          )}
          {showPercentRM && (
            <p className="p4 mobile-hidden">
              {load && status === "completed" && defaultRepMax
                ? `${round((load / defaultRepMax) * 100, 1)}%`
                : ""}
            </p>
          )}
          {showE1RM && (
            <p className="p4 mobile-hidden">
              {e1rm && status === "completed"
                ? withWeight(
                    round(fromMetric(e1rm, units, false), 1),
                    units,
                    false
                  )
                : ""}
            </p>
          )}
          {showPercentRM && !hideVelocitySettings && (
            <p className="p4">
              {velocity && status === "completed"
                ? `${getVelocityValue(velocity)}m/s`
                : ""}
            </p>
          )}
          <p className="p4 video">
            {!isExpanded && `${videoCount === 0 ? "-" : videoCount}`}
            {video && isExpanded && (
              <ExerciseVideoLine
                vidItem={video}
                indexIndividualSet={i}
                exerciseToEdit={exercise._id}
                setIndex={idx}
                src="/img/new-icons/play-gradient.svg"
              />
            )}
          </p>
          <CommentComponent
            comment={
              !isExpanded
                ? `${commentCount === 0 ? "-" : commentCount}`
                : comment
            }
            truncateLength={40}
          />
          {equalOutcomesEx && (
            <p className="p4 expand-outcomes mobile-hidden" />
          )}
        </div>
      </div>
    );
  });
};

export default OutcomesComponent;
